import { useReducer } from "react";
import reducer from "./reducer";
import ContactUsContext, { internalState } from "./context";
import { execute } from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import { IContactUsQuery } from "../../models/contactus/query";
import { IContactUs } from "../../models/contactus/response";
import { IUpdateContactUs } from "../../models/contactus/request";

export interface IProps {
  children: React.ReactNode;
}

const ContactUsContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
        const { data } = await EndPoints.contactUs.getAllContactUs(state.query);
        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const deleteContactUs = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
        await EndPoints.contactUs.deleteContactUs(id);
        getData();
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const getDetails = async (id: number) => {
      await execute({
        callback: async () => {
          dispatch({ type: "LOADING", payload: { loading: "details" } });
  
          const { data } = await EndPoints.contactUs.getContactUs(id);
  
          dispatch({ type: "SET_DETAILS", payload: { details: data } });
        },
        fallback: (error) => {},
        finallyCallback: () => {
          dispatch({ type: "LOADING", payload: { loading: "details" } });
        },
        throwException: false,
      });
    };
  
    const setDetails = async (data?: IContactUs) => {
      dispatch({ type: "SET_DETAILS", payload: { details: data } });
    };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IContactUsQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  const updateContactUs = async (id: number, payload: IUpdateContactUs) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });

        const { data } = await EndPoints.contactUs.updateContactUs(id, payload);

        if (state.details?.id === id) {
          dispatch({ type: "SET_DETAILS", payload: { details: data } });
        }

        getData();
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  return (
    <ContactUsContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          deleteContactUs,
          setSearch,
          setQuery,
          getDetails,
          setDetails,
          updateContactUs
        },
      }}
    >
      {props.children}
    </ContactUsContext.Provider>
  );
};

export default ContactUsContextProvider;
